import { FC, useEffect, useState } from 'react'
import { PageHeader } from '@lmc/ui-kit'
import { reportingConstants } from 'locale/reporting'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import styled from 'styled-components'
import { DownloadBulk, DownloadCard } from './components'
import { useRouter } from 'hooks/useRouter'
import { Row } from 'antd'
import { DownloadModal } from './components/DownloadModal'
import { useSelector } from 'react-redux'
import { Carer, Home, UserRole } from '@lmc/ts-common'
import { getNominative } from 'common/utils'
import { CSV_EXPORT_TYPE } from './components/utils'
import useHomeType from 'hooks/useHomeType'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { useRoleAccess } from 'components/AccessControl'

export const DownloadScreen: FC = () => {
  const { t } = useTranslation(NAMESPACE.REPORTING)

  const flags = useFeatureFlags({
    flagNames: ['outcomes_and_goals_v2'],
  })?.data

  const { hasRota } = useHomeType()

  const flagsOutcomesAndGoalsV2 = flags?.outcomes_and_goals_v2?.enabled

  const [CSVExportType, setCSVExportType] = useState<
    CSV_EXPORT_TYPE | undefined
  >(undefined)

  const preferredPeopleNominative: string = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative || 'Service Users'
  )

  const preferredCarePlansNominative: string = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredCarePlansNominative || 'Care Plans'
  )

  const user = useSelector(
    (state: { global: { user: Carer & { privacyOfficer?: boolean } } }) =>
      state.global.user
  )

  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const homes = useSelector(
    (state: { data: { homes: Home[] } }) => state.data.homes
  )

  const isGroupDownload = [UserRole.groupAdmin, UserRole.groupManager].includes(
    user.role
  )

  const hasCarePlansEnabled = home?.features?.carePlans?.enabled

  const hasPro = home?.features?.pro?.enabled

  const hasGoals =
    home?.features?.goals?.enabled ||
    (isGroupDownload && homes?.some((home) => home.features?.goals?.enabled))

  const handleToggleModal = (csvType: CSV_EXPORT_TYPE | undefined) =>
    setCSVExportType(csvType)

  const handleCloseModal = () => setCSVExportType(undefined)

  const isSeniorCarer = useRoleAccess([UserRole.seniorCarer])

  const cards = [
    {
      id: CSV_EXPORT_TYPE.TEAM_MEMBER,
      title: t(reportingConstants.TEAM_MEMBERS_TITLE),
      desc: t(reportingConstants.TEAM_MEMBERS_DESC),
    },
    {
      id: CSV_EXPORT_TYPE.RESIDENT,
      title: preferredPeopleNominative,
      desc: t(reportingConstants.RESIDENTS_DESC, {
        variable: getNominative(preferredPeopleNominative, 'Resident'),
        variable2: getNominative(
          preferredPeopleNominative.toLocaleLowerCase(),
          'Resident'
        ),
      }),
    },
    {
      id: CSV_EXPORT_TYPE.LOGS,
      title: t(reportingConstants.LOGS_TITLE),
      desc: t(reportingConstants.LOGS_DESC),
    },
  ]

  if (
    (hasPro && hasCarePlansEnabled) ||
    (isGroupDownload &&
      homes?.some((home) => home.features?.carePlans?.enabled))
  ) {
    cards.push(
      {
        id: CSV_EXPORT_TYPE.RISK_ASSESSMENT,
        title: t(reportingConstants.RISK_ASSESSMENTS_TITLE),
        desc: t(reportingConstants.RISK_ASSESSMENTS_DESC),
      },
      {
        id: CSV_EXPORT_TYPE.CARE_PLAN,
        title: preferredCarePlansNominative,
        desc: t(reportingConstants.CARE_PLANS_DESC, {
          variable: getNominative(preferredCarePlansNominative, 'Care Plans'),
        }),
      }
    )
  }

  if (hasPro && hasRota && !isSeniorCarer) {
    cards.push(
      {
        id: CSV_EXPORT_TYPE.ROSTERING_HISTORY,
        title: t(reportingConstants.ROS_HISTORY_TITLE),
        desc: t(reportingConstants.ROS_HISTORY_DESC),
      },
      {
        id: CSV_EXPORT_TYPE.UPCOMING_ROTA,
        title: t(reportingConstants.UP_ROTA_TITLE),
        desc: t(reportingConstants.UP_ROTA_DESC),
      }
    )
  }

  if (hasGoals && flagsOutcomesAndGoalsV2) {
    cards.push({
      id: CSV_EXPORT_TYPE.OUTCOME_AND_GOALS,
      title: t(reportingConstants.OUTCOME_GOALS_TITLE),
      desc: t(reportingConstants.OUTCOME_GOALS_DESC),
    })
  }

  const router = useRouter()

  const hasRolePermission = useRoleAccess(
    [UserRole.careHomeAdmin, UserRole.groupAdmin, UserRole.groupManager],
    [
      {
        i: 'view',
        this: 'Report',
      },
    ]
  )

  useEffect(() => {
    if (!hasRolePermission) {
      router.navigate('/')
    }
  }, [hasRolePermission])

  return (
    <MainContainer>
      <PageHeader title={t(reportingConstants.DOWNLOAD_CENTRE)}>
        <DownloadBulk cards={cards} />
      </PageHeader>
      <CardsWrapper>
        <Row gutter={24}>
          {cards?.map((card) => (
            <DownloadCard
              title={card.title}
              desc={card.desc}
              onClick={() => handleToggleModal(card.id)}
              key={card.id}
            />
          ))}
        </Row>
      </CardsWrapper>
      <DownloadModal
        isOpen={!!CSVExportType}
        onClose={handleCloseModal}
        CSVExportType={CSVExportType}
        preferredPeopleNominative={preferredPeopleNominative}
        preferredCarePlansNominative={preferredCarePlansNominative}
      />
    </MainContainer>
  )
}

const MainContainer = styled.div`
  max-width: 1080px;
  margin: 24px;
`

const CardsWrapper = styled.div`
  margin-top: 24px;
`

export default DownloadScreen
